import BaseModel from '@/services/api/BaseModel'
import Store from '@/store'
export default class Chat extends BaseModel {
  constructor() {
    super()
    this._model = 'chat'
  }

  /**
   * User external sid from store
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get externalIdentity() {
    return Store.state.Widget.externalIdentity
  }

  /**
   * Widget state from store
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get widgetState() {
    return Store.state.Widget.widgetState
  }

  /**
   * Last target rule
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get lastTargetRule() {
    return Store.state.Widget.lastTargetRule
  }

  /**
   * Channel Sid from store
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get channelSid() {
    return Store.state.LiveChat.channelSid
  }

  /**
   * Twilio identity from store
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get twilioIdentity() {
    return Store.state.LiveChat.userIdentity
  }

  /**
   * Retrieve prequalification skill
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get preQualificationSkill() {
    return Store.state.PreQualification.skillRouting
  }

  /**
   * Retrieve prequalification token
   * @readonly
   * @memberof Chat
   * @return {String}
   */
  get preQualificationToken() {
    return Store.state.PreQualification.surveyRecordToken
  }

  get botConversationId() {
    return Store.state.Bot.botConversationId
  }

  /**
   * Get widget configuration
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  getWidgetConfiguration() {
    return this.get(`${this.resourceUrl}/configurations/widget/parameters`)
  }

  /**
   * Get user identity
   * @returns {Promise<String>}
   * @memberof Chat
   */
  getExternalIdentity() {
    return this.post(`${this.resourceUrl}/contacts`)
  }

  /**
   * Get teaser labels
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  getTeaserLabel(type) {
    return this.get(
      `${this.resourceUrl}/configurations/messages/teaser/${type}`,
    )
  }

  /**
   * Get widget labels
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  getFullframeLabels(type) {
    return this.get(
      `${this.resourceUrl}/configurations/messages/fullframe/${type}`,
    )
  }

  /**
   * Retrieve a token
   * @return {Promise<string>}
   * @memberof Chat
   */
  getChatToken() {
    return this.post(`${this.resourceUrl}/tokens`, {
      contactSid: this.externalIdentity,
      twilioIdentity: this.twilioIdentity,
    })
  }

  /**
   * Call backend to create a chat room and ticket
   * @param {String} message
   * @param {Object} messageAttributes
   * @param {Int} [skillId=null]
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  createChatRoom({
    message,
    messageAttributes,
    skillId = null,
    history = null,
  }) {
    return this.post(`${this.resourceUrl}/`, {
      message: {
        contactSid: this.externalIdentity,
        message,
        messageAttributes: JSON.stringify(messageAttributes),
        skillId: this.preQualificationSkill || skillId,
        surveyRecordToken: this.preQualificationToken,
      },
      history,
    })
  }

  /**
   * Check agent availability, return true if:
   * - chat is open AND
   *   - agent is available
   *   - OR the system queue is open and place remain
   * @param {Int} [skillId=null]
   * @param {Boolean} [returnDissuasionMessage=false]
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  checkAvailability(skillId = null) {
    return this.get(`${this.resourceUrl}/v2/availability`, {
      params: {
        contactSid: this.externalIdentity,
        skillId: this.preQualificationSkill || skillId,
        targetRuleSid: this.lastTargetRule || '',
        widgetState: this.widgetState,
      },
    })
  }

  /**
   * Check if chat is open
   * @returns {Promise<Object>}
   * @memberof Chat
   */
  checkChatOpen() {
    return this.get(`${this.resourceUrl}/isChatOpen`)
  }

  /**
   * Delete channel
   * @param {String} closedBy
   * @returns {Promise}
   * @memberof Chat
   */
  deleteChannel(closedBy) {
    return this.put(`${this.resourceUrl}/rooms/${this.channelSid}`, {
      contactSid: this.externalIdentity,
      twilioIdentity: this.twilioIdentity,
      closedBy,
    })
  }

  /**
   * Add email for sending chat by email
   * @param {String} sendTo
   * @returns {Promise}
   * @memberof Chat
   */
  requestSendMail(sendTo) {
    return this.post(`${this.resourceUrl}/requestSendMail`, {
      sendTo,
      contactSid: this.externalIdentity,
      twilioIdentity: this.twilioIdentity,
      channelSid: this.channelSid,
    })
  }

  /**
   * Sending conversation by email when it's closed
   * @param {String} sendTo
   * @returns {Promise}
   * @memberof Chat
   */
  sendConversationByMail(sendTo) {
    return this.post(`${this.resourceUrl}/sendConversationByMail`, {
      sendTo,
      contactSid: this.externalIdentity,
      twilioIdentity: Store.state.LiveChat.oldUserIdentity,
      channelSid: this.channelSid,
    })
  }

  /**
   * Remove email to send chat by email
   * @returns {Promise}
   * @memberof Chat
   */
  cancelSendMail() {
    return this.put(`${this.resourceUrl}/cancelSendMail`, {
      contactSid: this.externalIdentity,
      twilioIdentity: this.twilioIdentity,
      channelSid: this.channelSid,
    })
  }

  /**
   * Get satisfaction survey (json format)
   * @returns {Promise}
   * @memberof Chat
   */
  getSatisfactionSurvey({ channelSid }) {
    return this.get(`${this.resourceUrl}/satisfaction`, {
      params: {
        channelSid,
      },
    })
  }

  /**
   * Save satisfaction survey
   * @param {String} token
   * @param {Boolean} surveyComplete
   * @param {Object} surveyJsResponses
   * @returns {Promise}
   * @memberof Chat
   */
  saveSatisfactionSurvey(token, surveyJsResponses, surveyComplete) {
    const formData = new FormData()
    formData.append('token', token)
    formData.append('surveyJsResponses', JSON.stringify(surveyJsResponses))
    formData.append('surveyComplete', surveyComplete)
    return this.post(
      `${this.service}/satisfaction/surveyRecord/save`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  }

  /**
   * Get preQualification (json format)
   * @returns {Promise}
   * @memberof Chat
   */
  getPreQualification(surveyId) {
    return this.get(`${this.service}/satisfaction/survey/${surveyId}`)
  }

  /**
   * Get preQualification (json format)
   * @returns {Promise}
   * @memberof Chat
   */
  savePreQualification(surveyId, surveyJsResponses) {
    const formData = new FormData()
    formData.append('surveyJsResponses', JSON.stringify(surveyJsResponses))
    formData.append('surveyId', surveyId)
    return this.post(`${this.service}/prequalification/save`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
  /**
   * @param {String} content
   * @param {Date} timestamp
   * @param {Object} attributes
   * @param {String|Number} [skillId=null]
   * @returns
   * @memberof Chat
   */
  botNewConversation({ content, timestamp, attributes }, skillId = null) {
    return this.post(
      `${this.resourceUrl}/bot/newConversation`,
      {
        message: content,
        contactSid: this.externalIdentity,
        skillId: this.preQualificationSkill || skillId,
        messageAttributes: JSON.stringify(attributes),
        messageTime: timestamp.toISOString(),
      },
      { headers: { botConversationId: this.botConversationId } },
    )
  }

  /**
   * @param {String} content
   * @param {Date} timestamp
   * @param {Object} attributes
   * @returns
   * @memberof Chat
   */
  botNewMessage({ content, timestamp, attributes }) {
    return this.post(
      `${this.resourceUrl}/bot/newMessage`,
      {
        message: content,
        contactSid: this.externalIdentity,
        messageAttributes: JSON.stringify(attributes),
        messageTime: timestamp.toISOString(),
      },
      { headers: { botConversationId: this.botConversationId } },
    )
  }

  botCloseConversation(closedBy, botConversationId = null) {
    return this.post(
      `${this.resourceUrl}/bot/closeConversation`,
      {
        contactSid: this.externalIdentity,
        closedBy,
      },
      {
        headers: {
          botConversationId: botConversationId || this.botConversationId,
        },
      },
    )
  }

  botEscalationConversation({ skillId = null, history = null }) {
    return this.post(
      `${this.resourceUrl}/bot/escalateConversation`,
      {
        message: {
          contactSid: this.externalIdentity,
          skillId: this.preQualificationSkill || skillId,
          surveyRecordToken: this.preQualificationToken,
        },
        history,
      },
      { headers: { botConversationId: this.botConversationId } },
    )
  }
}
